import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { ACCEPTED_UPLOAD_EXTENSIONS, isAcceptedUpload, MAX_UPLOAD_BYTES, MAX_UPLOAD_LABEL, MAX_UPLOAD_FILES, MAX_UPLOAD_FILES_MESSAGE, MAX_UPLOAD_FILES_CHIP, isTiedAllocation, effectiveProfileId } from '../../../utils/uploadNames';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Grid,
  Alert,
  CircularProgress,
  LinearProgress,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { collection, query, where, onSnapshot, orderBy, updateDoc, doc, documentId, runTransaction, deleteField } from 'firebase/firestore';
import { db } from '../../../services/firebase';
import { registerUploadBatch } from '../../../services/uploadRegistration';
import UploadRegistrationErrors from './UploadRegistrationErrors';
import { formatText } from '../../common/TextFormatterProvider';
import AllocationCard from './AllocationCard';
import ExistingFilesPrompt from '../../common/ExistingFilesPrompt';
import { findExistingFiles } from '../../../services/existingFiles';
import { settleEach, describeFailures } from '../../../utils/perMember';
import BulkDeleteConfirmDialog, { collectBulkDeleteTargets } from './BulkDeleteConfirmDialog';
import FileCard from './FileCard';
import FileReviewDialog from './FileReviewDialog';
import HelpButton from '../../common/HelpButton';
import { downloadFailureMessage } from '../../../services/api_utils';
import { deleteFile, gestureFailureMessage } from '../../../services/cleaningGestures';
import { downloadFiles } from '../../../services/fileDownloads';
import { isActiveProfile, buildProfileNameById, profileName } from '../../../utils/profileDisplay';
import { allocationAttributes, missingMandatoryAttributes } from '../../../utils/mandatoryAttributes';
import { sortNewestFirst } from '../../../utils/filePages';
import useFileSectionPages from '../../../hooks/useFileSectionPages';

const buildDocumentIdKey = (ids) => JSON.stringify(
  Array.from(new Set(ids.filter(Boolean))).sort()
);

const parseDocumentIdKey = (key) => JSON.parse(key);

function SmartUploadFiles({ dataProfiles, companyId, tableId, table }) {
  const [files, setFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [unallocatedFiles, setUnallocatedFiles] = useState([]);
  const [conflictingFiles, setConflictingFiles] = useState([]);   // every chosen file that already exists
  // The selection the duplicate check ran on: the dialog's counts and both of
  // its actions read this, never `files`, so a pick made while the dialog is
  // up can neither be uploaded unchecked nor mislabel the conflicts.
  const [checkedFiles, setCheckedFiles] = useState([]);
  // The last pick held more files than one upload may take.
  const [tooManyFiles, setTooManyFiles] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  // Targets of the pending bulk delete: { files, skipped } | null.
  const [bulkDeletePrompt, setBulkDeletePrompt] = useState(null);
  // The approved staging documents this session follows, each merged with its file
  // document once approve_allocation has created it (`file_doc_id` then names it).
  const [approvedFiles, setApprovedFiles] = useState([]);
  const fileInputRef = useRef(null);
  const unallocatedSelections = useRef(new Map());
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [splitSheets, setSplitSheets] = useState(false);
  const [selectedFileIds, setSelectedFileIds] = useState(new Set());
  const [isBulkDownloadingSelected, setIsBulkDownloadingSelected] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  // The file whose review dialog is open, hosted here rather than in the cards so that
  // a status change during review cannot take an open dialog down with its card.
  const [reviewFileId, setReviewFileId] = useState(null);

  // Selection surfaces (manual Select, approve gates) restrict to Active profiles;
  // name resolution spans the full list so deleted profiles still render a name.
  const activeProfiles = useMemo(() => (dataProfiles || []).filter(isActiveProfile), [dataProfiles]);
  const nameById = useMemo(() => buildProfileNameById(dataProfiles), [dataProfiles]);

  // Listener lifetime is determined only by which files are being watched.
  // Status snapshots replace file objects, but do not change these stable keys.
  const pendingFileIdsKey = buildDocumentIdKey(
    approvedFiles
      .filter(file => !file.file_doc_id)
      .map(file => file.unallocatedId || file.id)
  );
  const approvedFileIdsKey = buildDocumentIdKey(
    approvedFiles.map(file => file.file_doc_id)
  );

  // "Approved & Processing" is one section, shown a page at a time, newest upload first
  // (utils/filePages). No unit document is subscribed: a card is drawn from its file.
  const approvedSection = useMemo(() => ({ approved: sortNewestFirst(approvedFiles) }), [approvedFiles]);
  const { pages, next: nextPage, previous: previousPage } = useFileSectionPages(approvedSection, tableId);
  const approvedPage = pages.approved;

  // Watch unallocated spreadsheets
  useEffect(() => {
    if (!companyId || !tableId) return;

    const unallocatedQuery = query(
      collection(db, `users/${companyId}/tables/${tableId}/unallocated_files`),
      orderBy('created_at', 'desc')
    );

    const unsubscribe = onSnapshot(unallocatedQuery, (snapshot) => {
      const files = snapshot.docs.map(doc => {
        const data = doc.data();
        const fileId = doc.id;
        const userSelection = unallocatedSelections.current.get(fileId);

        return {
          id: fileId,
          ...data,
          fileName: data.name || data.fileName || 'Unknown File',
          // A backend-written selection (auto-allocation, or an approval from
          // another tab) outranks the proposal; a live user selection outranks
          // both. A tie has no proposal to start on (see isTiedAllocation).
          selected_data_profile: userSelection ?? data.selected_data_profile
            ?? (isTiedAllocation(data) ? '' : data.proposed_data_profile),
        };
      });

      const approvedDocs = files.filter(f => f.status === 'approved');
      const pendingIds = new Set(files.filter(f => f.status !== 'approved').map(f => f.id));

      setApprovedFiles(prev => {
        // Retract transition-only entries whose staging doc has bounced back to
        // a non-approved state (approval revalidation failed): the allocation
        // card below is the live surface again, and a monitoring entry with no
        // file document would sit as a ghost forever. Entries already resolved to
        // a file document are never retracted here — the file listeners own those.
        let next = prev.filter(f => !(f.isTransitioning && !f.file_doc_id
          && pendingIds.has(f.unallocatedId || f.id)));

        // Seed monitoring for approved staging docs this session did not click
        // (backend auto-allocation, or another tab), exactly as
        // handleApproveAllocation does, deduped against known entries. The doc
        // is deleted seconds later by the backend; if a snapshot never observes
        // the transition the file is simply not monitored here — the same as a
        // page refresh today.
        const known = new Set(next.map(f => f.unallocatedId || f.id));
        const fresh = approvedDocs
          .filter(f => !known.has(f.id))
          .map(f => ({ ...f, unallocatedId: f.id, isTransitioning: true }));
        if (fresh.length > 0) next = [...next, ...fresh];

        return (next.length === prev.length && fresh.length === 0) ? prev : next;
      });

      setUnallocatedFiles(files.filter(f => f.status !== 'approved'));
    }, (error) => {
      console.error('Error watching unallocated files:', error);
      setUploadError('Failed to watch unallocated file updates');
    });

    return () => unsubscribe();
  }, [companyId, tableId]);

  // Watch for newly approved files to find their file documents
  useEffect(() => {
    if (!companyId || !tableId) return;

    const fileIdsToFind = parseDocumentIdKey(pendingFileIdsKey);
    if (fileIdsToFind.length === 0) return;

    const BATCH_SIZE = 30;
    const unsubscribers = [];
    for (let i = 0; i < fileIdsToFind.length; i += BATCH_SIZE) {
      const batchIds = fileIdsToFind.slice(i, i + BATCH_SIZE);
      const q = query(
        collection(db, `users/${companyId}/tables/${tableId}/files`),
        where(documentId(), 'in', batchIds)
      );

      const unsubscribe = onSnapshot(q, (snapshot) => {
        const filesById = new Map(snapshot.docs.map(fileDoc => [
          fileDoc.id,
          { ...fileDoc.data(), id: fileDoc.id }
        ]));
        if (filesById.size === 0) return;

        setApprovedFiles(prev => {
          let changed = false;
          const next = prev.map(file => {
            // approve_allocation creates the file document WITH the staging doc's id —
            // match exactly by id (name matching cross-wires duplicate filenames)
            const fileDoc = filesById.get(file.unallocatedId || file.id);
            if (!fileDoc || file.file_doc_id) return file;
            changed = true;
            return {
              ...file,
              ...fileDoc,
              id: fileDoc.id,
              file_doc_id: fileDoc.id,
              isTransitioning: false
            };
          });
          return changed ? next : prev;
        });
      });
      unsubscribers.push(unsubscribe);
    }

    return () => unsubscribers.forEach(unsubscribe => unsubscribe());
  }, [pendingFileIdsKey, companyId, tableId]);

  // Selections are kept only for what is on screen: a file leaving the page is
  // unticked, so bulk actions only ever act on the visible page.
  useEffect(() => {
    const visibleIds = new Set(approvedPage.files.map(file => file.id));
    setSelectedFileIds(prev => {
      const next = new Set(Array.from(prev).filter(id => visibleIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [approvedPage]);

  // Watch the approved files' documents (status updates and deletion)
  useEffect(() => {
    if (!companyId || !tableId) return;
    const foundFileIds = parseDocumentIdKey(approvedFileIdsKey);
    if (foundFileIds.length === 0) return;
    const BATCH_SIZE = 30;
    const unsubscribers = [];
    for (let i = 0; i < foundFileIds.length; i += BATCH_SIZE) {
      const batchIds = foundFileIds.slice(i, i + BATCH_SIZE);
      const q = query(
        collection(db, `users/${companyId}/tables/${tableId}/files`),
        where(documentId(), 'in', batchIds)
      );
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const batchIdSet = new Set(batchIds);
        const presentIds = new Set(snapshot.docs.map(d => d.id));
        const changedIds = new Set(snapshot.docChanges().map(change => change.doc.id));
        const batchMap = new Map(snapshot.docs.map(fileDoc => [
          fileDoc.id,
          { ...fileDoc.data(), id: fileDoc.id }
        ]));
        setApprovedFiles(prev => {
          let changed = false;
          const next = [];

          prev.forEach(file => {
            if (!batchIdSet.has(file.file_doc_id)) {
              next.push(file);
              return;
            }

            if (!presentIds.has(file.file_doc_id)) {
              changed = true;
              return;
            }

            if (!changedIds.has(file.file_doc_id)) {
              next.push(file);
              return;
            }

            const latest = batchMap.get(file.file_doc_id);
            changed = true;
            next.push({
              ...file,
              ...latest,
              id: latest.id,
              file_doc_id: latest.id
            });
          });

          return changed ? next : prev;
        });
      });
      unsubscribers.push(unsubscribe);
    }
    return () => unsubscribers.forEach(u => u());
  }, [approvedFileIdsKey, companyId, tableId]);

  const handleFilesChosen = (chosenFiles) => {
    // One upload owns the selection until it completes: its success path clears
    // `files`, so anything picked meanwhile would be wiped, never uploaded.
    if (isUploading) return;
    // The duplicate dialog is deciding about ONE selection: no other is taken
    // until it closes.
    if (confirmDialogOpen) return;
    const allFiles = Array.from(chosenFiles);
    const wrongType = allFiles.filter((file) => !isAcceptedUpload(file.name));
    const tooLarge = allFiles.filter((file) => isAcceptedUpload(file.name) && file.size > MAX_UPLOAD_BYTES);
    const validFiles = allFiles.filter((file) => isAcceptedUpload(file.name) && file.size <= MAX_UPLOAD_BYTES);

    if (validFiles.length > MAX_UPLOAD_FILES) {
      // A pick replaces the selection like any other; a rejected pick must
      // not leave the previous files armed behind this message.
      setFiles([]);
      setTooManyFiles(true);
      setUploadError(MAX_UPLOAD_FILES_MESSAGE);
      return;
    }

    setTooManyFiles(false);
    setFiles(validFiles);

    const problems = [];
    if (wrongType.length) {
      problems.push(`Some files were not of an accepted type (${ACCEPTED_UPLOAD_EXTENSIONS.join(', ')}) and were ignored.`);
    }
    if (tooLarge.length) {
      problems.push(`Max file size is ${MAX_UPLOAD_LABEL} — ignored: ${tooLarge.map((f) => f.name).join(', ')}.`);
    }
    setUploadError(problems.join(' '));
  };

  // The input is cleared the moment its files are read: a file input only fires
  // change when its value changes, so a pick the cap or the duplicate dialog
  // refused would otherwise block the same file from being chosen again.
  const handleFileSelect = (e) => {
    const chosen = Array.from(e.target.files);
    e.target.value = null;
    handleFilesChosen(chosen);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
    handleFilesChosen(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isUploading) setIsDraggingOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
  };

  const handleBatchUpload = async () => {
    setUploadError('');
    setUploadSuccess('');
    setIsUploading(true);

    if (files.length === 0) {
      setUploadError('Please select files to upload');
      setIsUploading(false);
      return;
    }

    try {
      // Every file is checked, not just up to the first hit, so the prompt can name
      // all the duplicates and offer to upload only the new ones (H28).
      const conflicts = await findExistingFiles({ companyId, tableId, files });
      if (conflicts.length > 0) {
        setCheckedFiles(files);
        setConflictingFiles(conflicts);
        setConfirmDialogOpen(true);
        setIsUploading(false);
        return;
      }

      await uploadFilesToUnallocated();
    } catch (err) {
      console.error('Upload error:', err);
      setUploadError(err.message || 'Failed to upload files');
    } finally {
      setIsUploading(false);
    }
  };

  // `uploadFiles` defaults to the whole chosen set; the duplicate dialog's "Skip
  // Duplicates" passes the subset that is new.
  const uploadFilesToUnallocated = async (uploadFiles = files) => {
    if (uploadFiles.length === 0) return;
    setUploadProgress({ current: 0, total: uploadFiles.length });
    try {
      const { failed } = await registerUploadBatch({
        companyId, tableId, files: uploadFiles, splitSheets,
        onProgress: (current, total) => setUploadProgress({ current, total }),
      });
      setFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = null;
      const registered = uploadFiles.length - failed.length;
      setUploadSuccess(registered > 0 ? `Successfully uploaded ${registered} file(s) for allocation` : '');
      if (failed.length > 0) {
        setUploadError(`${failed.length} file(s) did not upload — add them again: `
          + `${failed.map(({ file }) => file.name).join(', ')}.`);
      }
    } finally {
      setUploadProgress({ current: 0, total: 0 });
    }
  };

  // Both dialog actions land here: "Upload Anyway" with every chosen file, "Skip
  // Duplicates" with the chosen files minus the ones that already exist.
  const handleConfirmUpload = async (uploadFiles) => {
    setConfirmDialogOpen(false);
    setIsUploading(true);
    try {
      await uploadFilesToUnallocated(uploadFiles);
    } catch (err) {
      console.error('Upload error:', err);
      setUploadError(err.message || 'Failed to upload files');
    } finally {
      setIsUploading(false);
    }
  };

  const handleUnallocatedProfileChange = (fileId, newProfileId) => {
    unallocatedSelections.current.set(fileId, newProfileId);
    setUnallocatedFiles(files =>
      files.map(file =>
        file.id === fileId
          ? { ...file, selected_data_profile: newProfileId }
          : file
      )
    );
  };

  const handleApproveAllocation = async (fileId, selectedProfile) => {
    // membership check, not a sentinel-string check: the allocator's 'No Match'
    // proposal (or any stale/deleted id) must never reach approve_allocation
    if (!selectedProfile || !activeProfiles.some(p => p.id === selectedProfile)) {
      setUploadError('Please select a data profile before approving.');
      return;
    }
    const profileDoc = activeProfiles.find(p => p.id === selectedProfile);
    const file = unallocatedFiles.find(f => f.id === fileId);
    const missing = missingMandatoryAttributes(
      table?.attributeColumns || [], allocationAttributes(profileDoc, file));
    if (missing.length > 0) {
      setUploadError(`Cannot approve: data profile "${profileName(profileDoc)}" is missing `
        + `required attribute${missing.length > 1 ? 's' : ''}: ${missing.map(formatText).join(', ')}. `
        + 'Update the profile, then approve again.');
      return;
    }
    try {
      // Add to approved files state for monitoring
      const fileToApprove = unallocatedFiles.find(f => f.id === fileId);
      if (fileToApprove) {
        // Add to approved list for monitoring and remove from unallocated list
        setApprovedFiles(prev => [...prev, {
          ...fileToApprove,
          unallocatedId: fileId, // Keep original ID for stable key
          isTransitioning: true,
          status: 'approved',
        }]);
        setUnallocatedFiles(prev => prev.filter(f => f.id !== fileId));
      }

      const fileRef = doc(db, `users/${companyId}/tables/${tableId}/unallocated_files`, fileId);
      await updateDoc(fileRef, {
        selected_data_profile: selectedProfile,
        status: 'approved'
      });
      unallocatedSelections.current.delete(fileId);
    } catch (err) {
      console.error('Error approving allocation:', err);
      setUploadError('Failed to approve allocation');
      // Revert UI on failure
      const fileToRevert = approvedFiles.find(f => f.unallocatedId === fileId);
      if (fileToRevert) {
        setUnallocatedFiles(prev => [...prev, fileToRevert]);
        setApprovedFiles(prev => prev.filter(f => f.unallocatedId !== fileId));
      }
    }
  };

  const handleDeleteUnallocatedFile = async (fileId) => {
    try {
      const stagingRef = doc(db, `users/${companyId}/tables/${tableId}/unallocated_files`, fileId);
      const midHandover = await runTransaction(db, async (tx) => {
        const stagingSnap = await tx.get(stagingRef);
        if (!stagingSnap.exists()) return false;
        // An approved card is already being handed over: the backend has copied
        // the file into the raw file's folder and is about to create the
        // document that names it. Deleting the card now would have the cleanup
        // trigger sweep that copy out from under a raw file created a moment
        // later. The card disappears on its own when the handover finishes.
        if (stagingSnap.data().status === 'approved') return true;
        const uploadId = stagingSnap.data().upload_id;
        if (uploadId) {
          const uploadRef = doc(db, `users/${companyId}/tables/${tableId}/uploads/${uploadId}`);
          const uploadSnap = await tx.get(uploadRef);
          if (uploadSnap.exists() && uploadSnap.data().raw_manifest) {
            tx.update(uploadRef, { [`raw_manifest.${fileId}`]: deleteField() });
          }
        }
        tx.delete(stagingRef);
        return false;
      });
      // Say so rather than leaving the card sitting there looking ignored.
      if (midHandover) {
        setUploadError('This file is being approved into the table right now and '
          + 'cannot be deleted until that finishes. It will disappear from this '
          + 'list on its own.');
      }
    } catch (error) {
      console.error('Error deleting unallocated file:', error);
      setUploadError('Failed to delete unallocated file.');
    }
  };

  const handleApproveAllAllocations = async () => {
    // Only files the allocator has settled ('pending') and that carry a REAL
    // profile selection may approve — analysing/splitting cards and 'No Match'
    // proposals stay put instead of poisoning the batch mid-loop.
    const isRealProfile = (id) => !!id && activeProfiles.some(p => p.id === id);
    const isCompleteForFile = (file) => {
      const p = activeProfiles.find(pr => pr.id === effectiveProfileId(file));
      return !!p && missingMandatoryAttributes(
        table?.attributeColumns || [], allocationAttributes(p, file)).length === 0;
    };
    // effectiveProfileId: a tie with no selection is nobody's to approve.
    const pendingWithProfile = unallocatedFiles.filter(
      f => f.status === 'pending' && isRealProfile(effectiveProfileId(f)));
    const filesToApprove = pendingWithProfile.filter(
      isCompleteForFile);
    const heldBack = pendingWithProfile.length - filesToApprove.length;
    if (filesToApprove.length === 0) {
      if (heldBack > 0) {
        setUploadError(`No files approved — ${heldBack} file(s) have data profiles `
          + 'missing required attributes. Update the profiles, then approve again.');
      }
      return;
    }

    // Move the approvable set to approved state immediately for better UX
    setApprovedFiles(prev => [
      ...prev,
      ...filesToApprove.map(f => ({
        ...f,
        unallocatedId: f.id,
        isTransitioning: true,
        status: 'approved',
      }))
    ]);
    setUnallocatedFiles(prev => prev.filter(f => !filesToApprove.some(a => a.id === f.id)));

    // Each member on its own: one that cannot be written stays actionable and is
    // named, while the ones that were approved stay approved and monitored (M181).
    const { done, failed } = await settleEach(filesToApprove, async (file) => {
      const fileRef = doc(db, `users/${companyId}/tables/${tableId}/unallocated_files`, file.id);
      await updateDoc(fileRef, {
        selected_data_profile: file.selected_data_profile || file.proposed_data_profile,
        status: 'approved'
      });
      unallocatedSelections.current.delete(file.id);
    });
    if (failed.length > 0) {
      failed.forEach(({ item, error }) => console.error(`Error approving ${item.fileName}:`, error));
      // Only the members that were not written go back (additively — analysing/
      // splitting cards were never removed; the snapshot listener re-syncs shortly anyway)
      const failedIds = new Set(failed.map(({ item }) => item.id));
      setUnallocatedFiles(prev => [...prev, ...filesToApprove.filter(a => failedIds.has(a.id) && !prev.some(f => f.id === a.id))]);
      setApprovedFiles(prev => prev.filter(f => !failedIds.has(f.unallocatedId)));
      setUploadError(`Approved ${done.length} of ${filesToApprove.length} file(s). Could not approve: `
        + `${describeFailures(failed, (file) => file.fileName)}.`);
    }
    if (done.length > 0) {
      setUploadSuccess(`Successfully approved ${done.length} file(s)`
        + (heldBack > 0
          ? `. ${heldBack} file(s) not approved — their data profiles are missing required attributes.`
          : ''));
    }
  };

  // The selected files that have their documents, newest upload first.
  const selectedFileDocs = () => sortNewestFirst(
    approvedFiles.filter((file) => file.file_doc_id && selectedFileIds.has(file.id)),
  );

  // Each selected file, its originals and its tables' cleaned workbooks, in one zip.
  // Each file's units are read when the button is pressed (services/fileDownloads).
  const handleBulkDownloadSelected = async () => {
    setUploadError(null);
    setUploadSuccess(null);
    setIsBulkDownloadingSelected(true);
    try {
      const chosen = selectedFileDocs();
      if (chosen.length > 0) {
        const { savedCount, failed } = await downloadFiles(companyId, tableId, chosen, `${tableId}_smart_upload_selected.zip`);
        if (failed.length > 0) setUploadError(downloadFailureMessage(failed, savedCount));
      }
    } catch (err) {
      console.error('Bulk selected download error:', err);
      setUploadError('The download could not be prepared. Try again.');
    } finally {
      setIsBulkDownloadingSelected(false);
    }
  };

  // Delete is a two-step: the button collects the eligible files and names them in a
  // confirmation; only Confirm requests the deletions, which the table's queue then
  // carries out irreversibly.
  const requestBulkDelete = async () => {
    try {
      const targets = await collectBulkDeleteTargets({
        companyId,
        tableId,
        files: approvedFiles.filter((file) => file.file_doc_id),
        selectedFileIds,
      });
      setBulkDeletePrompt(targets);
    } catch (error) {
      setUploadError(gestureFailureMessage(error));
    }
  };

  // Each file's deletion is requested in its own transaction, one after another: a
  // write batch cannot read, and every request is judged on its live file (Spec 5
  // section 5). A refusal is reported and the rest continue. Picking another table
  // meanwhile does not stop the requests; the files home gives the new table a list
  // of its own, so what they report is not shown there.
  const handleBulkDeleteConfirmed = async () => {
    if (!bulkDeletePrompt) return;
    setIsBulkDeleting(true);
    const refusals = [];
    for (const target of bulkDeletePrompt.files) {
      try {
        await deleteFile(companyId, tableId, target.id);
      } catch (error) {
        refusals.push(`${target.label} (${gestureFailureMessage(error)})`);
      }
    }
    setSelectedFileIds(new Set());
    setBulkDeletePrompt(null);
    setIsBulkDeleting(false);
    if (refusals.length > 0) setUploadError(`Could not delete ${refusals.join('; ')}.`);
  };

  const clearSelections = () => {
    setSelectedFileIds(new Set());
  };

  // Approved files whose data has landed: their file documents read `completed`, so
  // every approved file counts, on this page or not. A card still waiting for its file
  // document has no status yet and is not completed.
  const completedFileIds = useMemo(() => new Set(
    approvedFiles
      .filter(file => file.file_doc_id && file.status === 'completed')
      .map(file => file.file_doc_id)
  ), [approvedFiles]);

  // Removes completed cards from this list only: their files stay in the table and in
  // the files list below. Dropping them from the list closes their listeners and
  // unticks the files.
  const handleClearCompleted = () => {
    setApprovedFiles(prev => prev.filter(file => !completedFileIds.has(file.file_doc_id)));
  };

  // The file cards redraw only when one of their own inputs changes (React.memo), so
  // this handler keeps its identity.
  const handleFileSelectChange = useCallback((checked, fileId) => {
    setSelectedFileIds(prev => {
      const next = new Set(prev);
      if (checked) next.add(fileId); else next.delete(fileId);
      return next;
    });
  }, []);

  return (
    <>
      {/* Smart Upload Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">
            Smart Upload
          </Typography>
          <HelpButton 
            docPath="data-page/uploading-data/upload-workflow" 
            tooltip="Upload Workflow Documentation"
          />
        </Box>
        
        {tooManyFiles && (
          <Box sx={{ mb: 1 }}>
            <Chip label={MAX_UPLOAD_FILES_CHIP} color="error" size="small" />
          </Box>
        )}
        <UploadRegistrationErrors companyId={companyId} tableId={tableId} />
        {uploadError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUploadError(null)}>
            {uploadError}
          </Alert>
        )}
        
        {uploadSuccess && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setUploadSuccess(null)}>
            {uploadSuccess}
          </Alert>
        )}

        {isUploading && uploadProgress.total > 0 && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress 
              variant="determinate" 
              value={(uploadProgress.current / uploadProgress.total) * 100} 
            />
            <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
              Uploading {uploadProgress.current} of {uploadProgress.total} files...
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch', mb: 2 }}>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ACCEPTED_UPLOAD_EXTENSIONS.join(',')}
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />
          <Box
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            sx={{
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              border: '2px dashed',
              borderColor: tooManyFiles ? 'error.main' : (isDraggingOver ? 'primary.main' : 'rgba(255, 255, 255, 0.23)'),
              borderRadius: 1,
              cursor: isUploading ? 'default' : 'pointer',
              p: 2,
              textAlign: 'center',
              backgroundColor: isDraggingOver ? 'action.hover' : 'transparent',
              transition: 'border-color 0.2s, background-color 0.2s',
              '&:hover': isUploading ? {} : {
                borderColor: 'primary.main',
                backgroundColor: 'action.hover'
              }
            }}
            onClick={() => { if (!isUploading) fileInputRef.current.click(); }}
          >
            <CloudUploadIcon sx={{ mb: 1 }} />
            <Typography variant="body2">
              {isUploading
                ? 'Uploading… new files can be added once it finishes'
                : files.length > 0
                ? `${files.length} file(s) selected`
                : isDraggingOver
                ? 'Drop files here'
                : 'Click or drag files to this area to upload'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={splitSheets}
                  onChange={(e) => setSplitSheets(e.target.checked)}
                />
              }
              label="Split sheets into files (spreadsheets only)"
            />
          </Box>
          <Button
            variant="contained"
            startIcon={<CloudUploadIcon />}
            onClick={handleBatchUpload}
            disabled={isUploading || files.length === 0}
          >
            Upload
          </Button>
        </Box>

        {/* Card section: capped, with its own scroll, so a long session of uploads
            cannot push the files list off the page. The side and bottom padding give the
            cards' shadows room inside the scroll box, and the equal negative margins keep
            the box's edges where the Paper's padding already puts them. */}
        {(unallocatedFiles.length > 0 || approvedFiles.length > 0) && (
          <Box sx={{
            maxHeight: '60vh',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            mt: 3,
            px: 2,
            mx: -2,
            pb: 2,
            mb: -2,
          }}>
            {/* Unallocated Files Grid */}
            {unallocatedFiles.length > 0 && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1">
                    Files Awaiting Allocation ({unallocatedFiles.length})
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleApproveAllAllocations}
                  >
                    Approve Allocated
                  </Button>
                </Box>
                <Grid container spacing={2}>
                  {unallocatedFiles.map((file) => (
                    <Grid item xs={12} sm={6} md={3} key={file.id}>
                      <AllocationCard
                        file={file}
                        profiles={activeProfiles}
                        nameById={nameById}
                        onProfileChange={handleUnallocatedProfileChange}
                        onApprove={handleApproveAllocation}
                        onDelete={handleDeleteUnallocatedFile}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Approved & Processing Files */}
            {approvedFiles.length > 0 && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1">
                    Approved & Processing ({approvedFiles.length})
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {completedFileIds.size > 0 && (
                      <Button variant="outlined" onClick={handleClearCompleted}>
                        Clear Completed ({completedFileIds.size})
                      </Button>
                    )}
                  </Box>
                </Box>
                {(() => {
                  const selectedCount = selectedFileIds.size;
                  if (selectedCount === 0) return null;
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="body2">
                        {selectedCount} {selectedCount === 1 ? 'file' : 'files'} selected
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={handleBulkDownloadSelected}
                          disabled={isBulkDownloadingSelected}
                        >
                          {isBulkDownloadingSelected ? <CircularProgress size={16} /> : 'Download'}
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={requestBulkDelete}
                          disabled={isBulkDeleting}
                        >
                          Delete
                        </Button>
                        <Button variant="text" size="small" onClick={clearSelections}>Clear</Button>
                      </Box>
                    </Box>
                  );
                })()}
                <Grid container spacing={2}>
                  {approvedPage.files.map((file) => (
                    <Grid item xs={12} md={6} xl={4} key={file.unallocatedId}>
                      <FileCard
                        // Until approve_allocation has created the file document, the
                        // card is a placeholder named after the staging document.
                        file={file.isTransitioning
                          ? { id: file.unallocatedId || file.id, name: file.name || file.fileName, isTemporary: true }
                          : file}
                        companyId={companyId}
                        tableId={tableId}
                        tableName={formatText(table?.name || tableId)}
                        profileNameById={nameById}
                        selected={selectedFileIds.has(file.id)}
                        onSelectChange={file.isTransitioning ? undefined : handleFileSelectChange}
                        onOpen={setReviewFileId}
                      />
                    </Grid>
                  ))}
                </Grid>
                {(approvedPage.hasPrevious || approvedPage.hasNext) && (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mt: 2 }}>
                    <Button size="small" disabled={!approvedPage.hasPrevious} onClick={() => previousPage('approved')}>
                      Previous page
                    </Button>
                    <Typography variant="body2">Page {approvedPage.pageNumber}</Typography>
                    <Button size="small" disabled={!approvedPage.hasNext} onClick={() => nextPage('approved')}>
                      Next page
                    </Button>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}
      </Paper>

      {/* The review dialog — outside the file grid so the card it was opened from can come and go */}
      {reviewFileId && table && (
        <FileReviewDialog
          key={reviewFileId}
          open
          companyId={companyId}
          tableId={tableId}
          table={table}
          fileId={reviewFileId}
          onClose={() => setReviewFileId(null)}
          onNotice={setUploadError}
        />
      )}

      <BulkDeleteConfirmDialog
        open={Boolean(bulkDeletePrompt)}
        targets={bulkDeletePrompt}
        tableName={formatText(table?.name || tableId)}
        busy={isBulkDeleting}
        onCancel={() => setBulkDeletePrompt(null)}
        onConfirm={handleBulkDeleteConfirmed}
      />

      <ExistingFilesPrompt
        open={confirmDialogOpen}
        conflicts={conflictingFiles.map((file) => ({ file }))}
        total={checkedFiles.length}
        scope="in this table"
        onCancel={() => setConfirmDialogOpen(false)}
        onSkip={() => {
          const skip = new Set(conflictingFiles);
          handleConfirmUpload(checkedFiles.filter((file) => !skip.has(file)));
        }}
        onUploadAnyway={() => handleConfirmUpload(checkedFiles)}
      />
    </>
  );
}

export default SmartUploadFiles;
